import React, {
  createContext,
  useEffect,
  useReducer,
  useRef,
  Dispatch,
  PropsWithChildren,
} from 'react'
import { initialRecentsState } from './recent-search'

export interface SearchResults {
  players: Record<string, unknown>[]
  teams: Record<string, unknown>[]
  scheds: Record<string, unknown>[]
  orgs: Record<string, unknown>[]
  venues: Record<string, unknown>[]
}

export interface RecentSearchItem {
  id?: string | number
  entityType?: string
  type?: string
  [key: string]: any
}

export interface RecentSearches {
  uid?: string | number
  terms: string[]
  items: RecentSearchItem[]
}

export interface SearchPagination {
  current_page?: number
  last_page?: number
  [key: string]: string | number | undefined
}

export interface SearchState {
  isActive: boolean
  searchText: string
  filter: string
  isTeamOrSchedulePage: boolean
  displayContents: boolean
  loadingResults: boolean
  loadingMore: boolean
  results: SearchResults
  numResults: number | false
  page: number
  pagination: SearchPagination
  error: string
  topResult: Record<string, unknown>
  totalResults: Record<string, number | undefined>
  isSeeAllActive: boolean
  recentSearches: RecentSearches
}

export type SearchAction =
  | { type: 'SET_IS_ACTIVE'; payload?: boolean }
  | { type: 'SET_SEARCH_TEXT'; payload: string }
  | { type: 'SET_FILTER'; payload: string }
  | { type: 'SET_IS_TEAM_OR_SCHEDULE_PAGE'; payload: boolean }
  | { type: 'SET_DISPLAY_CONTENTS'; payload: boolean }
  | { type: 'SET_LOADING_RESULTS'; payload: boolean }
  | { type: 'SET_LOADING_MORE'; payload: boolean }
  | { type: 'SET_RESULTS'; payload: SearchResults }
  | { type: 'APPEND_RESULTS'; payload: SearchResults }
  | { type: 'SET_NUM_RESULTS'; payload: number | false }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGINATION'; payload: SearchPagination }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_TOP_RESULT'; payload: Record<string, unknown> }
  | {
      type: 'SET_TOTAL_RESULTS'
      payload: Record<string, number | undefined>
    }
  | { type: 'SET_IS_SEE_ALL_ACTIVE'; payload: boolean }
  | { type: 'SET_RECENT_SEARCHES'; payload: RecentSearches }

const createEmptyResults = (): SearchResults => ({
  players: [],
  teams: [],
  scheds: [],
  orgs: [],
  venues: [],
})

const getIsTeamOrSchedulePage = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  return /team\/|schedule\//.test(window.location?.pathname ?? '')
}

const initialSearchState: SearchState = {
  isActive: false,
  searchText: '',
  filter: '',
  isTeamOrSchedulePage: getIsTeamOrSchedulePage(),
  displayContents: false,
  loadingResults: false,
  loadingMore: false,
  results: createEmptyResults(),
  numResults: false,
  page: 1,
  pagination: {},
  error: '',
  topResult: {},
  totalResults: {},
  isSeeAllActive: true,
  recentSearches: { ...(initialRecentsState as RecentSearches) },
}

const searchReducer = (
  state: SearchState,
  action: SearchAction
): SearchState => {
  switch (action.type) {
    case 'SET_IS_ACTIVE': {
      const payload = action.payload
      return {
        ...state,
        isActive: typeof payload === 'boolean' ? payload : !state.isActive,
      }
    }
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: action.payload }
    case 'SET_FILTER':
      return { ...state, filter: action.payload }
    case 'SET_IS_TEAM_OR_SCHEDULE_PAGE':
      return { ...state, isTeamOrSchedulePage: action.payload }
    case 'SET_DISPLAY_CONTENTS':
      return { ...state, displayContents: action.payload }
    case 'SET_LOADING_RESULTS':
      return { ...state, loadingResults: action.payload }
    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload }
    case 'SET_RESULTS':
      return { ...state, results: action.payload }
    case 'APPEND_RESULTS':
      return {
        ...state,
        results: {
          players: [...state.results.players, ...action.payload.players],
          teams: [...state.results.teams, ...action.payload.teams],
          scheds: [...state.results.scheds, ...action.payload.scheds],
          orgs: [...state.results.orgs, ...action.payload.orgs],
          venues: [...state.results.venues, ...action.payload.venues],
        },
      }
    case 'SET_NUM_RESULTS':
      return { ...state, numResults: action.payload }
    case 'SET_PAGE':
      return { ...state, page: action.payload }
    case 'SET_PAGINATION':
      return { ...state, pagination: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_TOP_RESULT':
      return { ...state, topResult: action.payload }
    case 'SET_TOTAL_RESULTS':
      return { ...state, totalResults: action.payload }
    case 'SET_IS_SEE_ALL_ACTIVE':
      return { ...state, isSeeAllActive: action.payload }
    case 'SET_RECENT_SEARCHES':
      return { ...state, recentSearches: action.payload }
    default:
      return state
  }
}

interface SearchContextValue extends SearchState {
  dispatch: Dispatch<SearchAction>
  setIsActive: (value?: boolean) => void
  setText: (value: string) => void
  setFilter: (value: string) => void
  searchInputRef: React.MutableRefObject<HTMLInputElement | null>
}

interface SearchContextProviderProps {
  children: React.ReactNode
  isLoggedIn: boolean
}

export const SearchContext = createContext<SearchContextValue | undefined>(
  undefined
)

const SearchContextProvider: React.FC<
  PropsWithChildren<SearchContextProviderProps>
> = ({ children, isLoggedIn }) => {
  const [state, dispatch] = useReducer(searchReducer, initialSearchState)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch({ type: 'SET_IS_ACTIVE', payload: false })
      dispatch({ type: 'SET_SEARCH_TEXT', payload: '' })
      dispatch({ type: 'SET_FILTER', payload: '' })
    }
  }, [dispatch, isLoggedIn])

  // Handle Cmd+K (Mac) / Ctrl+K (Windows/Linux) to focus search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isShortcut = isMac
        ? event.metaKey && event.key === 'k'
        : event.ctrlKey && event.key === 'k'

      if (isShortcut) {
        event.preventDefault()
        searchInputRef.current?.focus()
        dispatch({ type: 'SET_IS_ACTIVE', payload: true })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const setIsActive = (value?: boolean) => {
    dispatch({ type: 'SET_IS_ACTIVE', payload: value })
  }

  const setText = (value: string) => {
    dispatch({ type: 'SET_SEARCH_TEXT', payload: value })
  }

  const setFilter = (value: string) => {
    dispatch({ type: 'SET_FILTER', payload: value })
  }

  const value: SearchContextValue = {
    ...state,
    dispatch,
    setIsActive,
    setText,
    setFilter,
    searchInputRef,
  }

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}

export const useSearchContext = (): SearchContextValue => {
  const context = React.useContext(SearchContext)
  if (!context) {
    throw new Error(
      'useSearchContext must be used within SearchContextProvider'
    )
  }
  return context
}

export default SearchContextProvider
